// 카테고리 관리 페이지.
// 목록 + 인라인 편집 + 상하 이동(순서 변경) + soft delete.
// 신규 등록은 같은 페이지 상단의 작성 폼에서 처리해 별도 라우트 없이 한 화면에서 모두 해결한다.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CategoryDto, ImageUploadKind } from "@oo/shared";
import { IMAGE_UPLOAD_KIND } from "@oo/shared";
import { Button, Card, useConfirm } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import {
  createCategory,
  deleteCategory,
  listCategoriesAdmin,
  reorderCategories,
  updateCategory,
} from "@/features/catalog/catalogApi.ts";
import ImageUploader from "@/features/catalog/ImageUploader.tsx";
import { imageUrl } from "@/features/catalog/imageUrl.ts";
import { ApiError } from "@/api/client.ts";

const CATEGORY_KIND: ImageUploadKind = IMAGE_UPLOAD_KIND.CATEGORY;

// 카테고리 목록/정렬 관리 화면.
export default function CategoriesPage() {
  const qc = useQueryClient();
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: listCategoriesAdmin,
  });

  // 신규 등록 폼 상태.
  const [newName, setNewName] = useState("");
  const [newIconKey, setNewIconKey] = useState<string | null>(null);
  const [newActive, setNewActive] = useState(true);
  const [createError, setCreateError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: createCategory,
    onSuccess: () => {
      setNewName("");
      setNewIconKey(null);
      setNewActive(true);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ["admin", "categories"] });
    },
    onError: (err: unknown) => {
      setCreateError(err instanceof Error ? err.message : "등록 실패");
    },
  });

  async function onCreate() {
    const name = newName.trim();
    if (!name) {
      setCreateError("카테고리 이름을 입력하세요.");
      return;
    }
    createMut.mutate({ name, iconImageKey: newIconKey, isActive: newActive });
  }

  return (
    <div className="mx-auto flex max-w-screen-lg flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader title="카테고리" description="카테고리를 등록하고 노출 순서를 조정합니다." />

      <Card as="section">
        <h3 className="mb-3 text-sm font-semibold text-ink-900">신규 카테고리 등록</h3>
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <ImageUploader
            kind={CATEGORY_KIND}
            value={newIconKey}
            onChange={setNewIconKey}
            previewClassName="h-24 w-24"
          />
          <div className="flex flex-1 flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-600">이름</span>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={30}
                className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900 placeholder:text-ink-500"
                placeholder="예: 소고기"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
              />
              사용자 화면에 노출
            </label>
            {createError ? <p className="text-xs text-danger-700">{createError}</p> : null}
            <Button
              type="button"
              onClick={onCreate}
              loading={createMut.isPending}
              className="self-start"
            >
              {createMut.isPending ? "등록 중..." : "등록"}
            </Button>
          </div>
        </div>
      </Card>

      <section className="overflow-hidden rounded-lg border border-ink-200 bg-surface shadow-card">
        <div className="border-b border-ink-200 p-4 text-sm font-semibold text-ink-900">
          전체 카테고리 ({categories.length})
        </div>
        {isLoading ? (
          <p className="p-4 text-sm text-ink-600">불러오는 중...</p>
        ) : categories.length === 0 ? (
          <p className="p-4 text-sm text-ink-600">등록된 카테고리가 없습니다.</p>
        ) : (
          <ul className="divide-y divide-ink-200">
            {categories.map((cat, idx) => (
              <CategoryRow
                key={cat.id}
                cat={cat}
                canMoveUp={idx > 0}
                canMoveDown={idx < categories.length - 1}
                onMoveUp={() => moveCategory(qc, categories, idx, idx - 1)}
                onMoveDown={() => moveCategory(qc, categories, idx, idx + 1)}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// 순서 변경: 두 항목의 sort_order 를 서로 바꾸는 것이 아니라,
// 인접 항목과 위치를 교환하는 식으로 단순 처리한다. 서버에 reorder 일괄 전송.
async function moveCategory(
  qc: ReturnType<typeof useQueryClient>,
  list: CategoryDto[],
  fromIdx: number,
  toIdx: number,
) {
  const next = [...list];
  const [moved] = next.splice(fromIdx, 1);
  if (!moved) {
    return;
  }
  next.splice(toIdx, 0, moved);
  // 위치를 그대로 sortOrder 로 매긴다 (0..N-1). 향후 드래그 정렬 도입 시에도 동일하게 사용.
  const items = next.map((c, i) => ({ id: c.id, sortOrder: i }));
  await reorderCategories({ items });
  await qc.invalidateQueries({ queryKey: ["admin", "categories"] });
}

// 카테고리 단일 행 컴포넌트.
// 인라인 편집: 이름/노출/아이콘. 변경 사항이 있으면 저장 버튼이 활성화된다.
function CategoryRow({
  cat,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
}: {
  cat: CategoryDto;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.name);
  const [iconKey, setIconKey] = useState<string | null>(cat.iconImageKey);
  const [active, setActive] = useState(cat.isActive);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () =>
      updateCategory(cat.id, {
        name: name !== cat.name ? name : undefined,
        iconImageKey: iconKey !== cat.iconImageKey ? iconKey : undefined,
        isActive: active !== cat.isActive ? active : undefined,
      }),
    onSuccess: async () => {
      setEditing(false);
      setError(null);
      await qc.invalidateQueries({ queryKey: ["admin", "categories"] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "저장 실패");
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteCategory(cat.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "categories"] });
    },
    onError: (err: unknown) => {
      // 속한 상품 있어서 409 인 경우 사용자에게 안내.
      if (err instanceof ApiError && err.code === "CONFLICT") {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "삭제 실패");
      }
    },
  });

  const url = imageUrl(cat.iconImageKey);

  return (
    <li className="flex flex-col gap-3 p-4 md:flex-row md:items-start">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 overflow-hidden rounded-md border border-ink-200 bg-ink-50">
          {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
        </div>
        {!editing ? (
          <div>
            <div className="text-sm font-medium text-ink-900">{cat.name}</div>
            <div className="text-xs text-ink-600">
              {cat.isActive ? "노출" : "숨김"} · 순서 {cat.sortOrder}
            </div>
          </div>
        ) : null}
      </div>
      {editing ? (
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-start">
            <ImageUploader
              kind={CATEGORY_KIND}
              value={iconKey}
              onChange={setIconKey}
              previewClassName="h-16 w-16"
            />
            <div className="flex flex-1 flex-col gap-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={30}
                className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900"
              />
              <label className="flex items-center gap-2 text-xs text-ink-700">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                사용자 화면 노출
              </label>
            </div>
          </div>
          {error ? <p className="text-xs text-danger-700">{error}</p> : null}
        </div>
      ) : null}
      <div className="ml-auto flex flex-wrap gap-2 text-xs">
        <RowButton onClick={onMoveUp} disabled={!canMoveUp}>
          위로
        </RowButton>
        <RowButton onClick={onMoveDown} disabled={!canMoveDown}>
          아래로
        </RowButton>
        {!editing ? (
          <>
            <RowButton onClick={() => setEditing(true)}>수정</RowButton>
            <button
              type="button"
              onClick={async () => {
                const ok = await confirm({
                  title: "카테고리 삭제",
                  description: `'${cat.name}' 카테고리를 삭제하시겠습니까?`,
                  confirmLabel: "삭제",
                });
                if (ok) {
                  deleteMut.mutate();
                }
              }}
              disabled={deleteMut.isPending}
              className="rounded border border-ink-300 px-2 py-1 text-danger-700 hover:bg-danger-50 disabled:opacity-50"
            >
              삭제
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
              className="rounded bg-brand-600 px-3 py-1 font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              저장
            </button>
            <RowButton
              onClick={() => {
                setName(cat.name);
                setIconKey(cat.iconImageKey);
                setActive(cat.isActive);
                setError(null);
                setEditing(false);
              }}
            >
              취소
            </RowButton>
          </>
        )}
        {!editing && deleteMut.isError && error ? (
          <p className="basis-full text-xs text-danger-700">{error}</p>
        ) : null}
      </div>
    </li>
  );
}

// 행 내 작은 보조 액션 버튼(토큰 테두리).
function RowButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded border border-ink-300 px-2 py-1 hover:bg-ink-100 disabled:opacity-40"
    >
      {children}
    </button>
  );
}
